import { BottomBarFeatures } from "src/models/BottomBarFeatures";
import { BottomBarBase } from "./bottom-bar-base";
import { EpubInstanceManagerService } from "src/app/services/instance/epub/epub-instance-manager.service";
import { IPageProgressionTimeline, IPageProgressionTimelineRangeData, IReaderView, ITransformAnimationOptions } from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-base";
import { BehaviorSubject, Observable, firstValueFrom } from "rxjs";
import { ToolbarFeatureModesModel } from "src/models/ToolbarFeatureModesModel";

export enum RendererName {
    /**
     * Responsive is not directly related to a specific Renderer, but means that ReaderView.setResponsiveRendererSelectionEnabled()
     * will be enabled, which allows the ReaderView to select the active renderer automatically.
     */
    RESPONSIVE = 'Responsive',
    STACK = 'Stack',
    FLIP_BOOK = 'FlipBook',
    SINGLE_PAGE_SWIPE = 'SinglePageSwipe',
    SPREAD_SWIPE = 'SpreadSwipe',
    SINGLE_DOCUMENT_SCROLL = 'SingleDocumentScroll'
}

export class BottomBarEpub extends BottomBarBase {
    private pageCount$: BehaviorSubject<number | null> = new BehaviorSubject<number | null>(null);
    private currentPage$: BehaviorSubject<number | null> = new BehaviorSubject<number | null>(null);
    private currentZoom$: BehaviorSubject<number | null> = new BehaviorSubject<number | null>(null);
    // NOTE
    // make sure it's the same one in the epub instance manager!
    private layoutMode$: BehaviorSubject<RendererName | null> = new BehaviorSubject<RendererName | null>(RendererName.SINGLE_PAGE_SWIPE);
    get pageCount(): Observable<number | null> { return this.pageCount$; }
    get currentPage(): Observable<number | null> { return this.currentPage$; }
    get currentZoom(): Observable<number | null> { return this.currentZoom$; }
    get layoutMode(): Observable<RendererName | null> { return this.layoutMode$; }

    zoomOptions: Array<number> = [100, 200, 500];
    avaliableLayoutModes: Array<ToolbarFeatureModesModel> = [
        { val: RendererName.SINGLE_PAGE_SWIPE, title: 'BOTTOMBAR.VIEW_SINGLEPAGE', icon: 'far fa-file' },
        { val: RendererName.FLIP_BOOK, title: 'BOTTOMBAR.VIEW_DOUBLEPAGE', icon: 'icon-dual-page-view' },
    ];

    protected override get hiddenFeatures(): BottomBarFeatures[] {
        return [
            BottomBarFeatures.CHANGE_FIT_MODE,
            BottomBarFeatures.ROTATE_CLOCKWISE,
            BottomBarFeatures.ROTATE_COUNTER_CLOCKWISE,
        ];
    }

    private readerView: IReaderView | undefined;
    private timeline: IPageProgressionTimeline | undefined;

    constructor(
        tabId: string,
        private readonly epubManager: EpubInstanceManagerService,
    ) {
        super(tabId);

        // get the colibrio viewer instance
        const inst = this.epubManager.getInstance(tabId);
        if (inst === null) {
            console.error('Could not find the instance for the pdf bottom bar !!');
            return;
        }
        this.readerView = inst.readerView;

        // listen to the colibrio events
        this.addEventsHandlers();

        this.epubManager.passBottomBarState(tabId, this.getUIState());

        // NOTE 18 Dec 2025, epub always starts at zoom 100%
        this.currentZoom$.next(100);
    }

    async toggleThumbsList() {
        const state = await firstValueFrom(this.getUIState());
        const thumbnailsActive = !!state[BottomBarFeatures.TOGGLE_THUMBNAILS]?.active;

        if (thumbnailsActive) {
            this.setFeaturesUnActive([BottomBarFeatures.TOGGLE_THUMBNAILS]);
        } else {
            this.setFeaturesActive([BottomBarFeatures.TOGGLE_THUMBNAILS]);
        }
    }

    async goToFirstPage() {
        this.readerView?.goToStart();
    }

    async goToPrevPage() {
        this.readerView?.previous();
    }

    async goToNextPage() {
        this.readerView?.next();
    }

    async goToLastPage() {
        if (!this.timeline) return;
        this.timeline.fetchLocatorFromPageIndex(this.timeline.getTotalNumberOfPages() - 1)
        .then((location) => {
            this.readerView?.goTo(location);
        });
    }

    setCurrentPageNumber(val: number): void {
        this.timeline?.fetchLocatorFromPageIndex(val - 1) // zero-based page index
        .then((location) => {
            this.readerView?.goTo(location);
        });
    }

    async addZoom(): Promise<void> {
        this.setZoom(0.1);
    }

    async removeZoom(): Promise<void> {
        this.setZoom(-0.1);
    }

    async setZoomLevel(_val: any): Promise<void> {
        this.setZoom(_val, true);
    }

    async setLayoutMode(mode: any) {
        const renderer = this.readerView?.getRendererByName(mode);
        if (renderer) {
            this.readerView?.setActiveRenderer(renderer);
            this.layoutMode$.next(mode);
        } else {
            console.error('Unable to find renderer with name: ', mode);
        }
    }

    private addEventsHandlers() {
        this.readerView?.addEngineEventListener('canPerformNextChanged', () => {
            const canPerform = this.readerView?.canPerformNext() ?? false;
            const features = [BottomBarFeatures.GO_NEXT_PAGE];
            canPerform ? this.setFeaturesEnabled(features) : this.setFeaturesDisabled(features);
        });
        this.readerView?.addEngineEventListener('canPerformPreviousChanged', () => {
            const canPerform = this.readerView?.canPerformPrevious() ?? false;
            const features = [BottomBarFeatures.GO_PREV_PAGE];
            canPerform ? this.setFeaturesEnabled(features) : this.setFeaturesDisabled(features);
        });
        this.readerView?.addEngineEventListener('canPerformGoToChanged', () => {
            const canPerform = this.readerView?.canPerformGoTo() ?? false;
            const features = [BottomBarFeatures.GO_FIRST_PAGE, BottomBarFeatures.GO_LAST_PAGE, BottomBarFeatures.GO_N_PAGE];
            canPerform ? this.setFeaturesEnabled(features) : this.setFeaturesDisabled(features);
        });
        this.readerView?.addEngineEventListener('pageProgressionTimelineRecalculated', () => {
            if (this.readerView) this.initTimeline(this.readerView);
        });
        this.readerView?.addEngineEventListener('visiblePagesChanged', () => {
            this.setCurrentPage();

            // NOTE 18 Dec 2025
            // if page changes, zoom is 100% again
            this.currentZoom$.next(100);
        });
    }

    private async initTimeline(readerView: IReaderView): Promise<void> {
        const pageProgressionTimeLine = readerView.getPageProgressionTimeline();
        if (!pageProgressionTimeLine) {
            console.error('Timeline could not be created to keep track of pages for epub.');
            this.setFeaturesHidden([BottomBarFeatures.GO_N_PAGE]);
            return;
        }
        this.timeline = pageProgressionTimeLine;
        this.pageCount$.next(pageProgressionTimeLine.getTotalNumberOfPages());
        this.setCurrentPage();
    }

    private setCurrentPage(): void {
        if (this.timeline) {
            const range: IPageProgressionTimelineRangeData = this.timeline.getVisibleTimelineRange();
            this.currentPage$.next(range.start.pageIndex + 1); // starts at 0
        }
    }

    private setZoom(val: number, useValExactly: boolean = false): void {
        const transformMngr = this.readerView?.getTransformManager();
        if (transformMngr?.canTransform()) {
            const el = this.readerView?.getElement();
            if (el) {
                const rect = el.getBoundingClientRect();
                const centerX = rect.x + (rect.width / 2);
                const centerY = rect.y + (rect.height / 2);
                const options: ITransformAnimationOptions = {
                    durationMs: 500
                }

                let newScale;
                if (!useValExactly) {
                    // zoom in/out
                    const activeTransform = transformMngr.getActiveTransform();
                    const scale = activeTransform?.scale ?? 1;
                    newScale =  Math.round((scale + val) * 10) / 10; // because somethimes scale is like 1.4567775432252

                    // if newScale is blow min and above max, fix that
                    const min = this.zoomOptions[0] / 100;
                    if (newScale < min) newScale = min;

                    const max = this.zoomOptions[this.zoomOptions.length - 1] / 100;
                    if (newScale > max) newScale = max;
                } else {
                    // set zoom level
                    newScale = val / 100;
                }
                newScale === 1
                    ? transformMngr?.removeActiveTransform()
                    : transformMngr?.zoomToClientPosition(centerX, centerY, newScale, options);

                // NOTE, for useValExactly, the zoom already passes a percentage, no need for the *100
                this.currentZoom$.next(useValExactly ? val : Math.round(newScale * 100)); // so it would not be 110.0000000000001
            }
        } else {
            console.error('Error - Can not zoom');
        }
    }
}